import React, { useState, useEffect, useRef } from 'react';

const Chats = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastChatCountRef = useRef(0);
  const lastSessionIdsRef = useRef(new Set());

  // Transform API response (sessions with messages) to flat chat objects
  const transformChats = (data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(session => {
      // Get the latest unread message or the latest message
      const messages = session.messages || [];
      const lastMsg = messages[messages.length - 1];
      const hasUnread = messages.some(m => !m.read && !m.isAdmin);
      
      return {
        sessionId: session.sessionId,
        name: session.name || 'Anonymous',
        email: session.email,
        message: lastMsg?.message || '',
        read: !hasUnread, // Session is "read" if all messages are read
        createdAt: session.lastMessage || lastMsg?.createdAt || new Date().toISOString(),
        messages: messages
      };
    });
  };

  // Force refresh - creates new array reference to trigger re-render
  const forceRefreshChats = async () => {
    try {
      const res = await fetch('/api/chats');
      const data = await res.json();
      
      // Transform the data structure
      const newChats = transformChats(data);
      
      // Track session IDs to detect new chats
      const currentSessionIds = new Set(newChats.map(c => c.sessionId));
      const lastSessionIds = lastSessionIdsRef.current;
      
      // Check if there are new sessions
      const hasNewChats = newChats.some(c => !lastSessionIds.has(c.sessionId));
      
      if (hasNewChats || newChats.length !== lastChatCountRef.current) {
        console.log('[Chats] New/updated chats detected, refreshing UI...');
      }
      
      lastSessionIdsRef.current = currentSessionIds;
      lastChatCountRef.current = newChats.length;
      
      // Create new array to ensure React detects change
      setChats([...newChats]);
      setLastRefresh(new Date());
      setLoading(false);
      console.log('[Chats] Loaded', newChats.length, 'sessions at', new Date().toISOString());
    } catch (err) {
      console.error('[Chats] Failed to fetch:', err);
      setLoading(false);
    }
  };

  // Use forceRefresh as primary fetch function
  const fetchChats = forceRefreshChats;

  useEffect(() => {
    // Initial fetch
    fetchChats();
    
    // WebSocket - connect to the chat backend (hardcoded for testing)
    const connectWS = () => {
      try {
        // Hardcoded WebSocket URL for testing - port 3001
        const wsUrl = `ws://192.168.10.28:80/ws`;
        console.log('[WS] Connecting to:', wsUrl);
        
        wsRef.current = new WebSocket(wsUrl);
        
        wsRef.current.onopen = () => {
          console.log('[WS] Connected to chat server');
          // Identify as NGX-MC dashboard
          wsRef.current.send(JSON.stringify({ type: 'admin_connect' }));
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('[WS] Received:', data);
            if (data.type === 'new_chat' || data.type === 'chat_message') {
              console.log('[WS] New chat event, refreshing...');
              forceRefreshChats();
            }
          } catch (e) {
            console.log('[WS] Parse error:', e);
          }
        };
        
        wsRef.current.onclose = () => { 
          console.log('[WS] Disconnected, reconnecting in 5s...');
          setTimeout(connectWS, 5000); 
        };
        
        wsRef.current.onerror = (err) => {
          console.log('[WS] Error (chat server may not be available):', err);
        };
      } catch (e) { 
        console.log('[WS] Failed to create:', e);
      }
    };
    
    // Start WebSocket
    connectWS();
    
    // Polling every 5 seconds as fallback
    console.log('[Chats] Starting polling interval...');
    const interval = setInterval(() => {
      console.log('[Polling] Fetching chats...');
      forceRefreshChats();
    }, 5000);
    
    return () => { 
      clearInterval(interval); 
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // Scroll to bottom when selecting a chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedChat]);

  const markAsRead = async (sessionId) => {
    try {
      // Mark all messages in session as read
      await fetch(`/api/chats/${sessionId}/read`, { method: 'PUT' });
      fetchChats();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const deleteChat = async (sessionId) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      const res = await fetch(`/api/chats/${sessionId}`, { method: 'DELETE' });
      console.log('[Chats] Delete response:', res.status, res.ok);
      setSelectedChat(null);
      fetchChats();
    } catch (err) {
      console.error('[Chats] Delete failed:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedChat) return;
    setSending(true);
    try {
      const res = await fetch('/api/chats/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: selectedChat.sessionId,
          email: selectedChat.email,
          message: replyMessage
        })
      });
      if (res.ok) {
        setReplyMessage('');
        fetchChats();
      } else {
        alert('Failed to send reply');
      }
    } catch (err) {
      alert('Error sending reply');
    }
    setSending(false);
  };

  const openChat = (chat) => {
    setSelectedChat(chat);
    if (!chat.read) {
      markAsRead(chat.sessionId);
    }
  };

  const unreadCount = chats.filter(c => !c.read).length;

  if (loading) return <div className="loading">Loading chats...</div>;

  return (
    <div className="chats-page">
      <div className="page-header">
        <h1>💬 Messages {unreadCount > 0 && <span className="badge">{unreadCount}</span>}</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchChats}>
            🔄 Refresh
          </button>
          {lastRefresh && (
            <span className="text-xs text-gray-500 ml-2">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="chats-container-modern">
        {/* Chat List Sidebar - styled like the website chat form */}
        <div className="chats-sidebar-modern glass-card rounded-2xl">
          <div className="sidebar-header-modern">
            <h3>Conversations</h3>
          </div>
          <div className="chats-list-modern">
            {chats.length === 0 ? (
              <div className="empty-state">
                <p>No messages yet. Messages from the website will appear here.</p>
              </div>
            ) : (
              chats.map(chat => (
                <div 
                  key={chat.sessionId} 
                  className={`chat-item-modern ${chat.read ? '' : 'unread'} ${selectedChat?.sessionId === chat.sessionId ? 'active' : ''}`}
                  onClick={() => openChat(chat)}
                >
                  <div className="chat-item-avatar-modern">
                    {(chat.name || 'A')[0].toUpperCase()}
                  </div>
                  <div className="chat-item-content-modern">
                    <div className="chat-item-header-modern">
                      <span className="chat-name-modern">{chat.name || 'Anonymous'}</span>
                      <span className="chat-time-modern">{new Date(chat.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="chat-item-preview-modern">
                      {chat.message?.substring(0, 40)}...
                    </div>
                    <div className="chat-item-email-modern">{chat.email}</div>
                  </div>
                  {!chat.read && <div className="unread-dot-modern"></div>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Detail View - styled like ChatWidget */}
        <div className="chat-detail-modern glass-card rounded-2xl">
          {selectedChat ? (
            <div className="chat-window-modern">
              {/* Header - gradient like the website */}
              <div className="chat-header-modern bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)]">
                <div className="chat-with-modern">
                  <strong>{selectedChat.name || 'Anonymous'}</strong>
                  <span className="status-online">● Online</span>
                </div>
                <div className="header-actions-modern">
                  <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => deleteChat(selectedChat.sessionId)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              {/* Messages area */}
              <div className="chat-messages-modern">
                {selectedChat.messages?.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`chat-message-modern ${msg.isAdmin ? 'admin' : 'user'}`}
                  >
                    <div className="msg-bubble-modern">
                      <div className="msg-sender-modern">
                        {msg.isAdmin ? 'NexGenX' : (selectedChat.name || 'Anonymous')}
                      </div>
                      <div className="msg-text-modern">{msg.message}</div>
                      <div className="msg-time-modern">{new Date(msg.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input area - rounded-full like the website */}
              <div className="chat-input-area-modern">
                <textarea
                  className="chat-input-modern rounded-full"
                  placeholder="Type a message..."
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  rows={1}
                />
                <button 
                  className="btn btn-primary rounded-full"
                  onClick={sendReply}
                  disabled={sending || !replyMessage.trim()}
                >
                  {sending ? '...' : 'Send'}
                </button>
              </div>
            </div>
          ) : (
            <div className="no-chat-selected-modern">
              <p>Select a conversation to view messages</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Chats;